import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Archive,
  Send,
  ArrowUp,
  ArrowDown,
  Eye,
  MousePointerClick,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  storiesAdminService,
  DEFAULT_STORY,
  type Story,
  type StoryStats,
  type StoryStatus,
} from '../../services/storiesAdminService';
import StoryCardPreview from './components/StoryCardPreview';

type StatusFilter = 'all' | StoryStatus;

const STATUS_LABELS: Record<StoryStatus, { label: string; cls: string }> = {
  draft: { label: 'Черновик', cls: 'bg-gray-100 text-gray-800' },
  published: { label: 'Опубликовано', cls: 'bg-green-100 text-green-800' },
  archived: { label: 'В архиве', cls: 'bg-amber-100 text-amber-800' },
};

const slugify = (s: string): string => {
  const cyrillicToLatin: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
    и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
    с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
    ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };
  return s
    .toLowerCase()
    .split('')
    .map((c) => cyrillicToLatin[c] ?? c)
    .join('')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const StoriesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>([]);
  const [statsByStory, setStatsByStory] = useState<Map<number, StoryStats>>(new Map());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [creating, setCreating] = useState(false);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, allStats] = await Promise.all([
        storiesAdminService.list(),
        storiesAdminService.statsAll(),
      ]);
      // Sort by display_order, then by id
      const sorted = [...(list || [])].sort((a, b) => {
        if (a.display_order !== b.display_order) return a.display_order - b.display_order;
        return a.id - b.id;
      });
      setStories(sorted);
      const m = new Map<number, StoryStats>();
      (allStats || []).forEach((s) => m.set(s.story_id, s));
      setStatsByStory(m);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Ошибка загрузки историй');
      setStories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const ts = Date.now();
      const created = await storiesAdminService.create({
        ...DEFAULT_STORY,
        slug: `story-${ts}`,
        title: 'Новая история',
        display_order: stories.length,
      });
      if (!created?.id) {
        toast.error('Не удалось создать историю');
        return;
      }
      toast.success('История создана');
      navigate(`/stories/${created.id}`);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка создания');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (story: Story) => {
    try {
      await storiesAdminService.publish(story.id);
      toast.success('История опубликована');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка публикации');
    }
  };

  const handleArchive = async (story: Story) => {
    try {
      await storiesAdminService.archive(story.id);
      toast.success('История в архиве');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка');
    }
  };

  const handleDelete = async (story: Story) => {
    if (!window.confirm(`Удалить историю «${story.title}»? Все слайды и события удалятся.`)) return;
    try {
      await storiesAdminService.remove(story.id);
      toast.success('История удалена');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка удаления');
    }
  };

  const handleMove = async (story: Story, direction: -1 | 1) => {
    if (reordering) return;
    const idx = stories.findIndex((s) => s.id === story.id);
    const newIdx = idx + direction;
    if (idx < 0 || newIdx < 0 || newIdx >= stories.length) return;
    setReordering(true);
    const reordered = [...stories];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setStories(reordered);
    try {
      await storiesAdminService.reorder(reordered.map((s) => s.id));
    } catch (e: any) {
      toast.error('Не удалось сохранить порядок');
      load();
    } finally {
      setReordering(false);
    }
  };

  const filtered = filter === 'all' ? stories : stories.filter((s) => s.status === filter);

  const formatDate = (s: string | null | undefined) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stories</h1>
          <p className="text-sm text-gray-500 mt-1">
            Карточки в дашборде в стиле Instagram. Управляйте порядком, слайдами и статусом.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
        >
          <Plus className="h-5 w-5" />
          {creating ? 'Создание...' : 'Создать историю'}
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {(['all', 'draft', 'published', 'archived'] as StatusFilter[]).map((f) => {
          const count =
            f === 'all' ? stories.length : stories.filter((s) => s.status === f).length;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                filter === f
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? 'Все' : STATUS_LABELS[f as StoryStatus].label} ({count})
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-gray-400 text-4xl mb-3">📱</div>
            <div className="text-gray-700 font-medium mb-1">
              {stories.length === 0 ? 'Историй пока нет' : 'Нет историй по фильтру'}
            </div>
            <p className="text-sm text-gray-500">
              {stories.length === 0
                ? 'Создайте первую — она появится в карусели на дашборде.'
                : 'Поменяйте фильтр или создайте новую.'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Порядок</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Превью</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Заголовок</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Тип</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Аудитория</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Импр / CTA</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Обновлено</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((s, idx) => {
                const stat = statsByStory.get(s.id);
                const canMoveUp = filter === 'all' && idx > 0;
                const canMoveDown = filter === 'all' && idx < filtered.length - 1;
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleMove(s, -1)}
                          disabled={!canMoveUp || reordering}
                          title="Выше"
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(s, 1)}
                          disabled={!canMoveDown || reordering}
                          title="Ниже"
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <StoryCardPreview
                        title={s.title}
                        coverEmoji={s.cover_emoji}
                        coverGradient={s.cover_gradient}
                        cardTextColor={s.card_text_color}
                        status={s.status}
                        size="sm"
                      />
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <Link
                        to={`/stories/${s.id}`}
                        className="font-medium text-gray-900 hover:text-blue-600"
                      >
                        {s.title}
                      </Link>
                      <div className="text-xs text-gray-500 font-mono">{s.slug}</div>
                    </td>
                    <td className="px-3 py-3">
                      {s.kind === 'system' ? (
                        <span
                          className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700"
                          title="Динамический сторис — рендер с per-user данными в момент открытия"
                        >
                          Системный
                        </span>
                      ) : (
                        <span
                          className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700"
                          title="Статический контент. Поддерживает expires_at — для anonsов до даты."
                        >
                          Анонс
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_LABELS[s.status].cls}`}
                      >
                        {STATUS_LABELS[s.status].label}
                      </span>
                      {/* Запланированный публиш / срок жизни — мини-бэйджи под статусом. */}
                      {(() => {
                        const now = new Date();
                        const tags: React.ReactNode[] = [];
                        if (s.published_at) {
                          const pub = new Date(s.published_at);
                          if (pub > now) {
                            tags.push(
                              <span key="scheduled" className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 text-blue-700" title={`Публикация запланирована на ${pub.toLocaleString('ru-RU')}`}>
                                ⏰ {pub.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                              </span>
                            );
                          }
                        }
                        if (s.expires_at) {
                          const exp = new Date(s.expires_at);
                          if (exp > now) {
                            tags.push(
                              <span key="expires" className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-700" title={`Истекает ${exp.toLocaleString('ru-RU')}`}>
                                ⌛ до {exp.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                              </span>
                            );
                          } else {
                            tags.push(
                              <span key="expired" className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-50 text-red-700" title={`Истёк ${exp.toLocaleString('ru-RU')}`}>
                                истёк
                              </span>
                            );
                          }
                        }
                        return tags;
                      })()}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 capitalize">
                      {s.target_audience}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5" />
                          {stat?.impressions ?? 0}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MousePointerClick className="h-3.5 w-3.5" />
                          {stat?.cta_clicks ?? 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">{formatDate(s.updated_at)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Link
                        to={`/stories/${s.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="h-4 w-4" />
                        Редактировать
                      </Link>
                      {s.status !== 'published' ? (
                        <button
                          type="button"
                          onClick={() => handlePublish(s)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-green-600 hover:bg-green-50 rounded ml-1"
                          title="Опубликовать"
                        >
                          <Send className="h-4 w-4" />
                          Опубликовать
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleArchive(s)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-amber-700 hover:bg-amber-50 rounded ml-1"
                          title="В архив"
                        >
                          <Archive className="h-4 w-4" />В архив
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(s)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded ml-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export { slugify };
export default StoriesListPage;
